import { FullCourse } from 'server/types';
import { AsyncForm, FormSubSection, InputLabel, Subtext } from '@components/async-form';
import { rpx } from 'client/lib/rpx-client';
import { BtnPrimary } from '@components/buttons';
import { showToast } from '@components/toaster';
import { HeadingSecondary } from '@course/components/headings';
import { useAsyncState } from 'client/lib/hooks';
import { Spinner } from '@components/spinner';
import { useState } from 'preact/hooks';
import { Toggle } from '@components/toggle';

const store = rpx.courseSettings;

type Props = {
  course: FullCourse;
  onUpdate?: (patch: Partial<FullCourse>) => void;
};

export function CompletionGateSettings({ course, onUpdate }: Props) {
  const [settings, setSettings] = useAsyncState(
    () => store.getCertGateSettings({ courseId: course.id }),
    [course.id],
  );

  const [enabled, setEnabled] = useState<boolean | undefined>(undefined);
  const [autoRemoval, setAutoRemoval] = useState<boolean | undefined>(undefined);
  const [moduleIds, setModuleIds] = useState<UUID[] | undefined>(undefined);
  const [lastEnforcedOn, setLastEnforcedOn] = useState<string | undefined>(undefined);
  const [buttonLabel, setButtonLabel] = useState<string | undefined>(undefined);
  const [deadlineText, setDeadlineText] = useState<string | undefined>(undefined);
  const [missingSubject, setMissingSubject] = useState<string | undefined>(undefined);
  const [missingEmail, setMissingEmail] = useState<string | undefined>(undefined);
  const [secondSubject, setSecondSubject] = useState<string | undefined>(undefined);
  const [secondEmail, setSecondEmail] = useState<string | undefined>(undefined);
  const [rolloverEmail, setRolloverEmail] = useState<string | undefined>(undefined);
  const [minDaysMissing, setMinDaysMissing] = useState<number | undefined>(undefined);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);

  if (!settings) {
    return (
      <div class="flex justify-center">
        <Spinner class="border-sky-500 w-8 h-8" />
      </div>
    );
  }

  const currentEnabled = enabled ?? settings.certGateEnabled;
  const currentAutoRemoval = autoRemoval ?? settings.certGateAutoRemoval;
  const currentModuleIds = moduleIds ?? settings.certGateModuleIds;
  const currentLastEnforcedOn = lastEnforcedOn ?? settings.certGateLastEnforcedOn;
  const currentButtonLabel = buttonLabel ?? settings.completeButtonLabel;
  const currentDeadlineText = deadlineText ?? settings.certGateDeadlineText;
  const currentMissingSubject = missingSubject ?? settings.certGateMissingSubject;
  const currentMissingEmail = missingEmail ?? settings.certGateMissingEmail;
  const currentSecondSubject = secondSubject ?? settings.certGateSecondWarningSubject;
  const currentSecondEmail = secondEmail ?? settings.certGateSecondWarningEmail;
  const currentRolloverEmail = rolloverEmail ?? settings.certGateRolloverEmail;
  const currentMinDaysMissing = minDaysMissing ?? settings.certGateMinDaysMissing;
  const modules = settings.modules;

  function toggleModule(moduleId: UUID) {
    const next = currentModuleIds.includes(moduleId)
      ? currentModuleIds.filter((id) => id !== moduleId)
      : [...currentModuleIds, moduleId];
    setModuleIds(next);
  }

  async function submit() {
    if (currentEnabled && currentAutoRemoval && currentModuleIds.length === 0) {
      setValidationError(
        'Pick at least one gated module before turning on automatic removal. With no modules selected, the gate has nothing to check.',
      );
      return;
    }
    setValidationError(undefined);
    setIsSaving(true);

    try {
      try {
        await store.setCertGateSettings({
          courseId: course.id,
          certGateEnabled: currentEnabled,
          certGateAutoRemoval: currentAutoRemoval,
          certGateModuleIds: currentModuleIds,
          certGateLastEnforcedOn: currentLastEnforcedOn,
          completeButtonLabel: currentButtonLabel,
          certGateDeadlineText: currentDeadlineText,
          certGateMissingSubject: currentMissingSubject,
          certGateMissingEmail: currentMissingEmail,
          certGateSecondWarningSubject: currentSecondSubject,
          certGateSecondWarningEmail: currentSecondEmail,
          certGateRolloverEmail: currentRolloverEmail,
          certGateMinDaysMissing: currentMinDaysMissing,
        });
      } catch (err) {
        const message = (err as { message?: unknown })?.message;
        if (typeof message === 'string' && message) {
          setValidationError(message);
          return;
        }
        throw err;
      }
      onUpdate?.({ certGateEnabled: currentEnabled });

      const fresh = await store.getCertGateSettings({ courseId: course.id });
      setSettings(fresh);

      setEnabled(undefined);
      setAutoRemoval(undefined);
      setModuleIds(undefined);
      setLastEnforcedOn(undefined);
      setButtonLabel(undefined);
      setDeadlineText(undefined);
      setMissingSubject(undefined);
      setMissingEmail(undefined);
      setSecondSubject(undefined);
      setSecondEmail(undefined);
      setRolloverEmail(undefined);
      setMinDaysMissing(undefined);

      showToast({
        title: 'Settings saved',
        message: 'Completion gate settings have been updated.',
        type: 'ok',
      });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <AsyncForm class="flex flex-col gap-8" onSubmit={submit}>
      <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
        <div class="flex flex-col gap-1">
          <HeadingSecondary>Completion gate</HeadingSecondary>
          <Subtext>
            Weekly deadline: Monday 11:59 PM Pacific. Reminder emails go out Sunday and Monday at
            8:00 AM Pacific to anyone with missing lessons. The removal check runs Wednesday.
          </Subtext>
        </div>

        <label class="flex items-start gap-3 cursor-pointer">
          <Toggle checked={currentEnabled} onClick={() => setEnabled(!currentEnabled)} />
          <span class="flex flex-col leading-snug">
            <strong>Enable completion gate</strong>
            <span class="opacity-75 text-sm">
              Turns on the weekly who's-behind report for guides and the Sunday and Monday reminder
              emails to students with gaps. Students also see this week's lessons, the deadline, and
              reminders for lessons not marked complete. No one is removed unless you also turn on
              automatic removal below.
            </span>
          </span>
        </label>

        <div class="pl-10">
          <label
            class={`flex items-start gap-3 ${currentEnabled ? 'cursor-pointer' : 'opacity-50'}`}
          >
            <Toggle
              checked={currentAutoRemoval}
              disabled={!currentEnabled}
              onClick={() => setAutoRemoval(!currentAutoRemoval)}
            />
            <span class="flex flex-col leading-snug">
              <strong>Automatically remove students who fall behind</strong>
              <span class="opacity-75 text-sm">
                Runs Wednesdays at 8:00 AM Pacific. Students still missing enough days of work lose
                course access only — billing is never changed, and no refund or cancellation
                happens. Each removed student receives the Wednesday removal email below.
              </span>
            </span>
          </label>
        </div>
      </div>

      <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
        <FormSubSection>
          <InputLabel>Gated modules</InputLabel>
          <Subtext>
            Only lessons in these modules count toward the gate. Each module counts as one day of
            work.
          </Subtext>
          <div class="flex flex-col gap-2 pt-1">
            {modules.length === 0 && (
              <span class="text-sm opacity-75">
                This course has no modules yet. Add modules first, then choose which ones the gate
                covers.
              </span>
            )}
            {modules.map((m) => {
              const isSelected = currentModuleIds.includes(m.id);
              return (
                <label
                  key={m.id}
                  class={`flex gap-3 cursor-pointer items-center border rounded-lg p-2 px-3 ${
                    isSelected ? 'border-indigo-500 ring-1 ring-indigo-500' : ''
                  }`}
                >
                  <input type="checkbox" checked={isSelected} onChange={() => toggleModule(m.id)} />
                  <span class="text-sm">
                    {m.title}
                    {m.isDraft && (
                      <span class="opacity-60"> — draft, not counted until published</span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </FormSubSection>

        <FormSubSection>
          <InputLabel>Remove when missing</InputLabel>
          <Subtext>
            Days of work (gated modules) a student must be missing at the Wednesday check to be
            removed. Students missing fewer still get both reminders and show as "Nearly there".
          </Subtext>
          <div class="flex items-center gap-2">
            <input
              class="ruz-input max-w-24"
              type="number"
              min={1}
              max={100}
              value={currentMinDaysMissing}
              onInput={(e) => {
                const value = parseInt((e.target as HTMLInputElement).value, 10);
                setMinDaysMissing(
                  Number.isNaN(value) ? undefined : Math.min(100, Math.max(1, value)),
                );
              }}
            />
            <span class="text-sm">days or more</span>
          </div>
        </FormSubSection>

        <FormSubSection>
          <InputLabel>Last removal day</InputLabel>
          <Subtext>
            A Wednesday. After it, the reminder emails and the removal check stop on their own.
            Leave blank to keep running every week until you turn the gate off.
          </Subtext>
          <input
            class="ruz-input max-w-60"
            type="date"
            value={currentLastEnforcedOn}
            onInput={(e) => setLastEnforcedOn((e.target as HTMLInputElement).value)}
          />
        </FormSubSection>
      </div>

      <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
        <HeadingSecondary>Student-facing wording</HeadingSecondary>

        <FormSubSection>
          <InputLabel>Mark-as-complete button label</InputLabel>
          <Subtext>
            Overrides the wording of the completion button on every lesson in this course. Leave
            blank to keep the default.
          </Subtext>
          <input
            class="ruz-input"
            type="text"
            value={currentButtonLabel}
            placeholder="Mark as complete"
            onInput={(e) => setButtonLabel((e.target as HTMLInputElement).value)}
          />
        </FormSubSection>

        <FormSubSection>
          <InputLabel>Deadline reminder text</InputLabel>
          <Subtext>
            Shown below the lesson navigation buttons on lessons in gated modules, and in the Sunday
            email — unless you write your own Sunday message below, which replaces it there.
          </Subtext>
          <input
            class="ruz-input"
            type="text"
            value={currentDeadlineText}
            placeholder="Complete this week's lessons by Monday 11:59 PM Pacific to maintain access"
            onInput={(e) => setDeadlineText((e.target as HTMLInputElement).value)}
          />
        </FormSubSection>

        <FormSubSection>
          <InputLabel>Sunday reminder subject</InputLabel>
          <Subtext>
            Leave blank for the default. Use <code>{'{count}'}</code> for the number of items each
            student is missing, <code>{'{items}'}</code> for "item" or "items", and{' '}
            <code>{'{course}'}</code> for the course title.
          </Subtext>
          <input
            class="ruz-input"
            type="text"
            value={currentMissingSubject}
            placeholder={`${course.title}: {count} {items} to finish by Monday night`}
            onInput={(e) => setMissingSubject((e.target as HTMLInputElement).value)}
          />
        </FormSubSection>

        <FormSubSection>
          <InputLabel>Sunday reminder message</InputLabel>
          <Subtext>
            Sent Sunday 8:00 AM Pacific. Edit it any time before then and that Sunday's email uses
            the new wording. It appears right under "Hi [first name],". Each student's own missing
            lessons, the reminder to hit the complete button, and the button back to the course are
            added below it automatically. Leave blank to use the generic message.
          </Subtext>
          <textarea
            class="ruz-input"
            rows={8}
            value={currentMissingEmail}
            placeholder={`e.g.\nWe're at the end of week 2, and these are the pieces you still need to check off. Everything below is due Monday at 11:59 PM Pacific.\n\nIf you're stuck on any of them, hit reply — we'd rather hear from you than lose you.`}
            onInput={(e) => setMissingEmail((e.target as HTMLTextAreaElement).value)}
          />
        </FormSubSection>

        <FormSubSection>
          <InputLabel>Monday reminder subject</InputLabel>
          <Subtext>Same tokens as the Sunday subject. Leave blank for the default.</Subtext>
          <input
            class="ruz-input"
            type="text"
            value={currentSecondSubject}
            placeholder={`${course.title}: {count} {items} still open, due tonight`}
            onInput={(e) => setSecondSubject((e.target as HTMLInputElement).value)}
          />
        </FormSubSection>

        <FormSubSection>
          <InputLabel>Monday reminder message</InputLabel>
          <Subtext>
            Sent Monday 8:00 AM Pacific to students still missing lessons. The same missing-lesson
            list and button are added below it. Leave blank to use the generic message.
          </Subtext>
          <textarea
            class="ruz-input"
            rows={8}
            value={currentSecondEmail}
            placeholder={`e.g.\nToday is the deadline for this week's work. Finish and mark complete everything below by 11:59 PM Pacific tonight to keep your place.\n\nIf something's in the way, hit reply and tell us.`}
            onInput={(e) => setSecondEmail((e.target as HTMLTextAreaElement).value)}
          />
        </FormSubSection>

        <FormSubSection>
          <InputLabel>Wednesday removal email</InputLabel>
          <Subtext>
            Sent when a student is automatically removed. Leave this blank to use the built-in
            sprint message, which names the next cohort start (the first Monday of January, March,
            May, July, September or November) on its own, so there is nothing to re-date each
            cohort. Write your own only if this course runs on a different calendar, and then name
            the start date yourself.
          </Subtext>
          <textarea
            class="ruz-input"
            rows={8}
            value={currentRolloverEmail}
            placeholder={`Built-in message:\nWe noticed this week's work wasn't all complete as of Sunday night. And if there were extenuating circumstances, or you think you got this by mistake, hit reply and tell me what happened, and we'll figure it out.\n\nOtherwise, we're moving you to the next sprint, which starts [next cohort start]. Nothing lost, and you start again from orientation.\n\n…\n\nJust reply to this email and we'll get you set up for [next cohort start].`}
            onInput={(e) => setRolloverEmail((e.target as HTMLTextAreaElement).value)}
          />
        </FormSubSection>
      </div>

      {validationError && (
        <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {validationError}
        </div>
      )}

      <footer>
        <BtnPrimary disabled={isSaving}>{isSaving ? 'Saving…' : 'Save'}</BtnPrimary>
      </footer>
    </AsyncForm>
  );
}
